import { Box, Heading, Panel } from '@hover/blueprint';
import React, { useEffect, useState } from 'react';
import { animated, useTrail } from 'react-spring';
import styled from 'styled-components';

import { MenuItemType } from 'src/modules/designStudio/constants/hardCodedData/textureData';
import { animationConfig } from 'src/modules/designStudio/helpers/animationConfig';
import { Button, Image } from 'src/shared/components';

import { List } from '../List';

type Item = {
  colorGroup?: string;
  name?: string;
  thumbnail?: string;
  textureId: number;
};

interface ColorPalletProps {
  items: Item[];
  selectedId: number;
  onPress: (textureId: number) => void;
}
interface ColorFamilyProps {
  title: string;
  items: string[];
  selectedGroup?: string;
  onPress: (group: string) => void;
}

interface ColorIconListProps {
  items: Item[];
  title: string;
  groups: string[];
  selectedMenuItem: MenuItemType;
  onPress: (textureId: number) => void;
}

const IconBtn = styled(Button)<{ isSelected: boolean }>`
  background-color: transparent;
  &::after {
    position: absolute;
    display: ${({ isSelected }) => (isSelected ? 'block' : 'none')};
    content: '';
    border-radius: inherit;
    top: -4px;
    right: -4px;
    bottom: -4px;
    left: -4px;
    border: 2px solid #72aeef;
  }
`;

const ImageContainer = styled(Box)`
  border-radius: 50%;
  overflow: hidden;
  width: 100%;
  height: 100%;
`;

const ColorFamilyPicker = ({
  items,
  onPress,
  title,
  selectedGroup,
}: ColorFamilyProps) => (
  <List title={title}>
    {items.map(item => (
      <Button
        fill={item === selectedGroup ? 'solid' : 'outline'}
        key={item}
        marginRight={200}
        onPress={() => onPress(item)}
      >
        {item}
      </Button>
    ))}
  </List>
);

const ColorPallet = ({ items, onPress, selectedId }: ColorPalletProps) => {
  const trails = useTrail(items.length, animationConfig.menuItemAnimation);

  return (
    <Panel
      display="grid"
      gridArea={100}
      gridRowGap={400}
      gridTemplateColumns="auto auto auto"
      justifyItems="center"
    >
      {trails.map((style, index) => (
        <animated.div key={items[index].textureId} style={style}>
          <Box key={items[index].textureId} padding={100}>
            <IconBtn
              height={88}
              isSelected={items[index].textureId === selectedId}
              label={`texture-${items[index].textureId}`}
              onPress={() => onPress(items[index].textureId)}
              shape="circle"
              // TODO - replace with actual selected styling
              width={88}
            >
              <ImageContainer>
                <Image
                  alt={items[index].name || 'texture'}
                  height="100%"
                  src={items[index].thumbnail}
                />
              </ImageContainer>
            </IconBtn>
          </Box>
        </animated.div>
      ))}
    </Panel>
  );
};

const TexturePicker = ({
  title,
  groups,
  items,
  selectedMenuItem,
  onPress,
}: ColorIconListProps) => {
  const [selectedColorGroup, setColorGroup] = useState<string | undefined>();

  useEffect(() => {
    const defaultGroup = selectedMenuItem.colorGroup || groups[0];
    setColorGroup(defaultGroup);
  }, [groups, selectedMenuItem.colorGroup]);

  const filteredItems = items.filter(
    item => item.colorGroup === selectedColorGroup,
  );

  return (
    <Box flexDirection="column">
      {groups.length > 0 ? (
        <ColorFamilyPicker
          items={groups}
          onPress={group => setColorGroup(group)}
          selectedGroup={selectedColorGroup}
          title={title}
        />
      ) : (
        <Heading marginBottom={400} size={200}>
          {title}
        </Heading>
      )}
      <ColorPallet
        items={filteredItems}
        onPress={onPress}
        selectedId={selectedMenuItem.textureId}
      />
    </Box>
  );
};

export { TexturePicker };
